import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { ActivityIndicator, Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { WindowInformation } from '../../../services/window-information';
import FormInput from '../../../components/FormInput';
import Toast from 'react-native-toast-message';
import { deacService, VacancyCancellationReasonsType } from '../../../services/central-api/deac';
import FilterDropdown from '../../../components/Filter/components/FilterDropdown';
import { AlreadyExists } from '../../../services/central-api/base-service';

interface ModalProps {
    isModalVisible: boolean;
    setModalVisible: Dispatch<SetStateAction<boolean>>;
    vacancyCancellationReasonId: number | undefined;
    onConfirm: () => void;
}

export default function VacancyCancellationReasonModal({ vacancyCancellationReasonId, isModalVisible, setModalVisible, onConfirm }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [name, setName] = useState<string>('');
    const [purpose, setPurpose] = useState<VacancyCancellationReasonsType | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isFetchingData, setIsFetchingData] = useState<boolean>(vacancyCancellationReasonId && vacancyCancellationReasonId > 0 ? true : false);

    function getSelectablePurposes(): { label: string; value: VacancyCancellationReasonsType | undefined; }[] {
        return [
            { label: translate('appAgentApplicationCancellationReason'), value: VacancyCancellationReasonsType.APPLICATION_APP_AGENT },
            { label: translate('webPanelApplicationCancellationReason'), value: VacancyCancellationReasonsType.APPLICATION_WEB_PANEL },
            { label: translate('webPanelVacancyCancellationReason'), value: VacancyCancellationReasonsType.VACANCY_WEB_PANEL }
        ];
    }

    async function getVacancyTag() {
        try {
            setIsFetchingData(true);
            const vacancyCancellationReason = await deacService.getVacancyCancellationReasonById(Number(vacancyCancellationReasonId));
            setName(vacancyCancellationReason.name);
            setPurpose(vacancyCancellationReason.purpose);
        } catch (err) {
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
            closeModal();
        } finally {
            setIsFetchingData(false);
        }
    }

    async function formSubmit() {
        try {
            if (!purpose) {
                return;
            }

            setIsLoading(true);

            if (vacancyCancellationReasonId) {
                await deacService.updateVacancyCancellationReason({ id: vacancyCancellationReasonId, name, purpose });
            } else {
                await deacService.createVacancyCancellationReason({ name, purpose });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            closeModal();
            onConfirm();
        } catch (err) {
            if (err instanceof AlreadyExists) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('cancellationReasonAlreadyExist'),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (isModalVisible && vacancyCancellationReasonId && vacancyCancellationReasonId > 0) {
            getVacancyTag();
        }
    }, [vacancyCancellationReasonId, isModalVisible]);

    function isFormValid(): boolean {
        if (name == '' || purpose == undefined) {
            return false;
        }

        return true;
    }

    const closeModal = () => {
        setModalVisible(false);
        setName('');
        setPurpose(null);
    };

    function getPurposeHint() {
        switch (purpose) {
            case VacancyCancellationReasonsType.APPLICATION_APP_AGENT: return translate('applicationAppAgentHintText');
            case VacancyCancellationReasonsType.APPLICATION_WEB_PANEL: return translate('applicationWebPanelHintText');
            case VacancyCancellationReasonsType.VACANCY_WEB_PANEL: return translate('vacancyWebPanelHintText');
            default: return '';
        }
    }

    return (
        <Modal
            transparent
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => closeModal()}
        >
            <View style={styles.container}>
                <View style={styles.formContainer(windowInfo.isMobile)}>
                    {isFetchingData ?
                        <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', minHeight: 250 }}>
                            <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                        </View>
                        : <>
                            <View style={{ flexDirection: 'column', rowGap: 8 }}>
                                <MyAppText style={styles.header}>{translate('cancellationReasonRegistration')}</MyAppText>
                                <MyAppText style={styles.subheader}>{translate('fillFieldsBelow')}</MyAppText>
                            </View>
                            <View style={{ flexDirection: 'column', rowGap: 24 }}>
                                <View style={{ flexDirection: 'column', rowGap: 8, zIndex: 2 }}>
                                    <FilterDropdown
                                        label={translate('purpose')}
                                        items={getSelectablePurposes()}
                                        multiple={false}
                                        setValue={setPurpose}
                                        value={purpose}
                                        zIndex={2}
                                    />
                                    {purpose && <MyAppText style={styles.purposeHintText}>{getPurposeHint()}</MyAppText>}
                                </View>
                                <FormInput
                                    labelStyle={styles.inputLabelStyle}
                                    onChangeText={setName}
                                    value={name}
                                    label={translate('reason')}
                                    disabled={!purpose}
                                    hintText={<View style={{ flexDirection: 'column', gap: 32 }}>
                                        <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('vacancyCancellationReasonHintText1')}</MyAppText>
                                        <MyAppText style={{ color: theme.colors.white['500'] }}>{translate('vacancyCancellationReasonHintText2')}</MyAppText>
                                    </View>}
                                />
                            </View>
                            <FormActions
                                viewStyle={{ flex: 1, justifyContent: 'space-around' }}
                                buttonStyle={{ paddingHorizontal: 16, paddingVertical: 9, }}
                                buttonContentContainerStyle={{ flex: 1 }}
                                onSubmit={formSubmit}
                                onClose={closeModal}
                                disabled={!isFormValid() || isLoading}
                                isLoading={isLoading}
                            />
                        </>}
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1,
        paddingHorizontal: 16,
    },
    formContainer: (isMobile: boolean) => ({
        width: isMobile ? '95%' : 450,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        paddingHorizontal: 16,
        paddingVertical: 24,
        rowGap: 32
    }),
    header: {
        fontSize: 18,
        fontWeight: '700',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
    },
    subheader: {
        fontSize: 14,
        fontWeight: '400',
        flexWrap: 'wrap',
        color: theme.colors.modal.subheader,
    },
    inputLabelStyle: {
        fontSize: 14,
        fontWeight: '600',
        flexWrap: 'wrap',
        color: theme.colors.modal.header,
        marginBottom: 8,
    },
    purposeHintText: {
        fontSize: 12,
        fontWeight: '400',
        color: theme.colors.neutral['400']
    }
}));
